import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

export default function Strutture() {
  const qc = useQueryClient();
  const { reload } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const { data = [] } = useQuery({ queryKey: ["strutture-mng"], queryFn: async () => (await api.get("/manage/strutture")).data });

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (form.id) await api.put(`/manage/strutture/${form.id}`, form);
      else await api.post("/manage/strutture", form);
      toast.success("Salvato"); setOpen(false);
      qc.invalidateQueries({ queryKey: ["strutture-mng"] }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const FIELDS = [["nome", "Nome struttura"], ["indirizzo", "Indirizzo"], ["comune", "Comune"], ["provincia", "Provincia"], ["cap", "CAP"], ["telefono", "Telefono"], ["email", "Email"], ["responsabile", "Responsabile"], ["posti_autorizzati", "Posti autorizzati"], ["posti_accreditati", "Posti accreditati"], ["camere", "Camere"], ["soglia_alvo", "Soglia allerta alvo (giorni)"], ["soglia_non_alim", "Soglia allerta non mangia/beve (giorni)"], ["soglia_flebo", "Soglia allerta aiuto sanitario (giorni)"], ["soglia_emergenza_pulizie", "Soglia emergenza pulizie (%)"], ["parametri_frequenza_default", "Frequenza parametri di default (giorni)"]];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div><h1 className="text-2xl font-head font-bold">Strutture</h1><p className="text-sm text-muted-foreground">{data.length} strutture</p></div>
        <Button onClick={() => { setForm({ soglia_alvo: 3, soglia_non_alim: 2, soglia_flebo: 3 }); setOpen(true); }} data-testid="new-struttura" className="font-semibold"><Plus size={18} className="mr-1" /> Nuova struttura</Button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[...data].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it")).map((s) => (
          <div key={s.id} className="gr-card p-5" data-testid={`struttura-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl grid place-items-center text-white font-bold" style={{ background: s.colore || "#1f7a5c" }}>{s.nome?.[0]}</div>
                <div><div className="font-head font-bold">{s.nome}</div><div className="text-xs text-muted-foreground">{s.comune} ({s.provincia})</div></div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setForm(s); setOpen(true); }} data-testid={`edit-struttura-${s.id}`}><Pencil size={15} /></Button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
              <div><span className="text-muted-foreground text-xs">Posti autorizzati</span><div className="font-bold tabnum">{s.posti_autorizzati || "—"}</div></div>
              <div><span className="text-muted-foreground text-xs">Posti accreditati</span><div className="font-bold tabnum">{s.posti_accreditati || "—"}</div></div>
              <div><span className="text-muted-foreground text-xs">Camere</span><div className="font-bold tabnum">{s.camere || "—"}</div></div>
              <div><span className="text-muted-foreground text-xs">Responsabile</span><div className="font-semibold">{s.responsabile || "—"}</div></div>
              <div><span className="text-muted-foreground text-xs">Soglia alvo</span><div className="font-semibold tabnum">{s.soglia_alvo || 3} gg</div></div>
              <div><span className="text-muted-foreground text-xs">Soglia non mangia/beve</span><div className="font-semibold tabnum">{s.soglia_non_alim || 2} gg</div></div>
              <div><span className="text-muted-foreground text-xs">Soglia aiuto sanitario</span><div className="font-semibold tabnum">{s.soglia_flebo || 3} gg</div></div>
              <div><span className="text-muted-foreground text-xs">Soglia emerg. pulizie</span><div className="font-semibold tabnum">{s.soglia_emergenza_pulizie || 20}%</div></div>
              <div><span className="text-muted-foreground text-xs">Freq. parametri default</span><div className="font-semibold tabnum">{s.parametri_frequenza_default ? `${s.parametri_frequenza_default} gg` : "—"}</div></div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Modifica struttura" : "Nuova struttura"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, l]) => (
              <div key={k} className={k === "nome" || k === "indirizzo" ? "col-span-2" : ""}>
                <Label className="text-xs font-semibold mb-1 block">{l}</Label>
                <Input value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} data-testid={`struttura-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-struttura" className="font-semibold">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
